import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import BottomSheet from 'components/DesignSystem/BottomSheet';
import Snackbar from 'components/DesignSystem/Snackbar';
import Section from 'components/Meteorology/Shared/ControlPanelSheet/Section';
import OptionRow from 'components/Meteorology/Shared/ControlPanelSheet/OptionRow';
import SheetActionButton from 'components/Layout/MobileShell/SheetActionButton';
import useAutomaticOptions from 'utils/hooks/flux/useAutomaticOptions';
import { useGetRevisionOptionsQuery } from 'services/api/products/flux';
import { useHandleRequestErrors } from 'utils/hooks';
import { ResponseError } from 'services/api/base';
import {
  selectFluxAutomaticControlPanelUnicenarioScenario,
  selectFluxAutomaticControlPanelSelectedRevision,
  setUnicenarioScenario,
  setRevisionDate,
} from 'redux/reducers/fluxAutomaticControlPanelReducer';

import './index.scss';

type SheetSection = 'scenario' | 'revision' | '';

export default function UnicenarioControlPanelSheet({
  open,
  dismiss,
}: {
  open: boolean;
  dismiss: () => void;
}) {
  const dispatch = useDispatch();
  const [openSection, setOpenSection] = useState<SheetSection>('scenario');

  const { labeledScenarios, isFetching } = useAutomaticOptions();
  const selectedScenario = useSelector(selectFluxAutomaticControlPanelUnicenarioScenario);
  const selectedRevision = useSelector(selectFluxAutomaticControlPanelSelectedRevision);

  const { data, error } = useGetRevisionOptionsQuery();
  const revisionOptions = data?.data ?? [];
  useHandleRequestErrors(error as ResponseError);

  useEffect(() => {
    if (open) setOpenSection('scenario');
  }, [open]);

  const toggleSection = (section: SheetSection) => () => (
    setOpenSection((current) => (current === section ? '' : section))
  );

  // Igual ao desktop (UnicenarioStudy): tocar no cenário já selecionado o desmarca.
  const selectScenario = (scenario: string) => {
    dispatch(setUnicenarioScenario(scenario === selectedScenario ? undefined : scenario));
    setOpenSection('revision');
  };

  return (
    <BottomSheet
      open={open}
      dismiss={dismiss}
      title="Painel de controle"
      className="flux-automatic-unicenario-sheet"
    >
      <div className="flux-automatic-unicenario-sheet-body">
        <Section
          label="Estudo Automático"
          valueText={selectedScenario}
          open={openSection === 'scenario'}
          onToggle={toggleSection('scenario')}
        >
          {isFetching && <Spinner animation="border" />}
          {!isFetching && labeledScenarios.map((group) => (
            <div className="flux-automatic-unicenario-sheet-group" key={group.label}>
              <p className="flux-automatic-unicenario-sheet-group-label">{group.label}</p>
              {group.scenarios.map((scenario) => (
                <OptionRow
                  key={scenario.scenario}
                  label={scenario.scenario}
                  selected={selectedScenario === scenario.scenario}
                  disabled={!scenario.selectable}
                  onSelect={() => selectScenario(scenario.scenario)}
                />
              ))}
            </div>
          ))}
        </Section>

        <Section
          label="Data de Revisão"
          valueText={selectedRevision || undefined}
          open={openSection === 'revision'}
          onToggle={toggleSection('revision')}
        >
          {revisionOptions.length === 0 && (
            <Snackbar className="mt-2">Nenhuma revisão disponível.</Snackbar>
          )}
          {revisionOptions.map((option) => (
            <OptionRow
              key={option}
              label={option}
              selected={option === selectedRevision}
              onSelect={() => {
                dispatch(setRevisionDate(option));
                setOpenSection('');
              }}
            />
          ))}
        </Section>
      </div>

      <div className="flux-automatic-unicenario-sheet-footer">
        <SheetActionButton variant="confirm" onClick={dismiss}>
          Aplicar filtros
        </SheetActionButton>
      </div>
    </BottomSheet>
  );
}
